export interface PalestraRuntimeConfig {
  relayPath: string;
}

interface RelayLocation {
  protocol: string;
  host: string;
  hostname: string;
}

interface ResolveRelayUrlOptions {
  runtimeValue: unknown;
  location: RelayLocation;
  development: boolean;
  developmentUrl?: string | undefined;
}

type RuntimeGlobal = typeof globalThis & {
  __PALESTRA_RUNTIME_CONFIG__?: unknown;
};

const RELAY_PATH_PATTERN = /^\/[A-Za-z0-9_-]+(?:\/[A-Za-z0-9_-]+)*$/;

export function parseRuntimeConfig(value: unknown): PalestraRuntimeConfig | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const relayPath = (value as { relayPath?: unknown }).relayPath;
  if (
    typeof relayPath !== "string" ||
    relayPath.length < 3 ||
    relayPath.length > 512 ||
    !RELAY_PATH_PATTERN.test(relayPath)
  ) {
    return undefined;
  }
  return { relayPath };
}

export function readRuntimeConfig(): PalestraRuntimeConfig | undefined {
  return parseRuntimeConfig((globalThis as RuntimeGlobal).__PALESTRA_RUNTIME_CONFIG__);
}

export function buildRelayWebSocketUrl(
  config: PalestraRuntimeConfig,
  location: Pick<RelayLocation, "protocol" | "host">,
): string | undefined {
  const protocol =
    location.protocol === "https:" ? "wss:" : location.protocol === "http:" ? "ws:" : undefined;
  if (!protocol || !location.host) return undefined;
  return `${protocol}//${location.host}${config.relayPath}`;
}

export function resolveRelayUrl(options: ResolveRelayUrlOptions): string | undefined {
  const runtime = parseRuntimeConfig(options.runtimeValue);
  if (runtime) return buildRelayWebSocketUrl(runtime, options.location);
  if (!options.development) return undefined;

  if (options.developmentUrl) {
    try {
      const configured = new URL(options.developmentUrl);
      if (configured.protocol === "ws:" || configured.protocol === "wss:") return configured.href;
    } catch {
      return undefined;
    }
    return undefined;
  }

  return options.location.hostname ? `ws://${options.location.hostname}:8787` : undefined;
}
